<template>
  <div id="app">
    <header class="management-nav" v-if="authenticated">
      <router-link class="management-brand" to="/Home" :aria-label="restaurantName + ': volver al inicio'"><img v-if="restaurantLogo && !logoFailed" :src="restaurantLogo" :alt="restaurantName" @error="logoFailed = true" /><span v-else aria-hidden="true">{{ restaurantInitials }}</span></router-link>
      <el-button class="management-menu-toggle" :aria-expanded="navigationOpen" aria-controls="management-navigation" aria-label="Abrir o cerrar navegación" @click="navigationOpen = !navigationOpen"><svg width="22" height="22" viewBox="0 0 24 24" aria-hidden="true"><path d="M3 5h18M3 12h18M3 19h18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" /></svg></el-button>
      <el-menu id="management-navigation" mode="horizontal" :router="true" :default-active="$route.path" :class="['management-navigation', { 'is-open': navigationOpen }]" @select="navigationOpen = false">
        <el-menu-item index="/Home" aria-label="Inicio"><i class="el-icon-house" aria-hidden="true" /><span class="home-label">Inicio</span></el-menu-item>
        <el-menu-item index="/Restaurante"><i class="el-icon-office-building" aria-hidden="true" /><span>Información general</span></el-menu-item>
        <el-menu-item index="/Grupos"><i class="el-icon-folder" aria-hidden="true" /><span>Grupos</span></el-menu-item>
        <el-menu-item index="/Articulos"><i class="el-icon-fork-spoon" aria-hidden="true" /><span>Artículos</span></el-menu-item>
        <el-menu-item index="/Menu"><i class="el-icon-notebook-2" aria-hidden="true" /><span>Menús</span></el-menu-item>
        <el-menu-item index="/PlatosMenu"><i class="el-icon-dish" aria-hidden="true" /><span>Platos menú</span></el-menu-item>
      </el-menu>
      <div class="management-account"><span>{{ name }}</span><el-button icon="el-icon-user" aria-label="Cambiar contraseña" title="Cambiar contraseña" @click="passwordDialog = true" /><el-button icon="el-icon-switch-button" aria-label="Cerrar sesión" title="Cerrar sesión" @click="logout" /></div>
    </header>
    <main :class="authenticated ? 'management-main' : 'management-login'">
      <div v-if="authenticated && mspAccess" class="management-msp-context"><span>Acceso desde MSP</span><el-button icon="el-icon-back" @click="returnToMsp">Volver al portal MSP</el-button></div>
      <header v-if="authenticated" class="management-page-heading"><div><p class="management-eyebrow">GESTIÓN DE LA CARTA</p><h1>{{ pageTitle }}</h1><p>{{ pageDescription }}</p></div></header>
      <router-view @authenticated="setAuthenticated" @profile-saved="loadRestaurantBrand" />
    </main>
    <el-dialog v-if="authenticated" title="Renovación pendiente" :visible.sync="renewalDialog" width="min(92%, 560px)" append-to-body><p>La licencia de tu carta caduca el <strong>{{ renewalDate }}</strong>.</p><p>Quedan {{ renewalNotice ? renewalNotice.daysRemaining : 0 }} días. Contacta con tu proveedor para tramitar la renovación y mantener la carta publicada.</p><p v-if="renewalNotice && renewalNotice.serial">Número de serie: {{ renewalNotice.serial }}</p><span slot="footer"><el-button type="primary" @click="renewalDialog = false">Entendido</el-button></span></el-dialog>
    <provider-footer />
    <password-change-dialog v-if="authenticated" :visible.sync="passwordDialog" @changed="logout" />
  </div>
</template>
<script>
import { getRestaurantProfile, imageUrl } from "./api/request";
import storage from "./api/storage.js";
import ProviderFooter from "./components/ProviderFooter.vue";
import PasswordChangeDialog from './components/PasswordChangeDialog.vue';
export default {
  components: { ProviderFooter, PasswordChangeDialog },
  mounted() {
    window.addEventListener('brb-session-ended', this.handleSessionEnded);
    document.addEventListener('visibilitychange', this.checkSessionExpiry);
    // El enlace MSP debe canjearse antes de comprobar una sesión anterior.
    if (window.location.hash.startsWith('#/msp-access')) return;
    this.authenticated = storage.loadAuthenticated() === "true";
    if (!this.authenticated) {
      if (!["login", "MspAccess"].includes(this.$route.name)) this.$router.replace({ name: "login" });
    } else {
      this.authenticated = true;
      this.name = storage.loadName();
      this.loadRestaurantBrand();
      this.checkSessionExpiry();
    }

  },
  beforeDestroy() {
    clearTimeout(this.sessionTimer);
    window.removeEventListener('brb-session-ended', this.handleSessionEnded);
    document.removeEventListener('visibilitychange', this.checkSessionExpiry);
  },
  data() {
    return {
      authenticated: false,
      sessionTimer: null,
      passwordDialog: false,
      renewalDialog: false,
      renewalChecked: false,
      renewalNotice: null,
      mspAccess: sessionStorage.getItem('br-ca-msp-session') === 'true' || !!sessionStorage.getItem('brb-msp-token'),
      navigationOpen: false,
      usuario: "",
      contraseña: "",
      restaurantLogo: "",
      restaurantName: "Restaurante",
      logoFailed: false,
      name: "",
    };
  },
  computed: {
    renewalDate() { return this.renewalNotice ? this.renewalNotice.expiresOn.split("-").reverse().join("/") : ""; },
    restaurantInitials() { return this.restaurantName.trim().split(/\s+/).slice(0, 2).map(word => word.charAt(0)).join('').toUpperCase(); },
    pageTitle() { return { Home: "Inicio", Restaurante: "Información general", Grupos: "Grupos de la carta", Articulos: "Artículos", Menu: "Menús", PlatosMenu: "Platos de menú" }[this.$route.name] || "Gestión"; },
    pageDescription() { return { Home: "Publicación y acceso a tu carta digital.", Restaurante: "La imagen y los datos de tu establecimiento.", Grupos: "Organiza las secciones y elige cuáles destacar en el inicio.", Articulos: "Gestiona los platos, sus precios, fotografías y alérgenos.", Menu: "Prepara tus menús y el orden de sus familias y platos.", PlatosMenu: "Organiza las familias de platos disponibles para crear tus menús." }[this.$route.name] || ""; }
  },
  watch: { '$route'() { this.navigationOpen = false; if(this.$route.name === 'login') this.handleSessionEnded(); } },
  methods: {
    handleSessionEnded() {
      clearTimeout(this.sessionTimer);
      this.authenticated = false;
      this.navigationOpen = false;
      this.passwordDialog = false;
      this.renewalDialog = false;
      this.renewalChecked = false;
      this.renewalNotice = null;
      this.mspAccess = false;
      this.name = '';
      this.restaurantLogo = '';
      this.restaurantName = 'Restaurante';
    },
    checkSessionExpiry() {
      clearTimeout(this.sessionTimer);
      if(!this.authenticated) return;
      const remaining = storage.sessionExpiresAt() - Date.now();
      if(remaining <= 0) { this.logout(); return; }
      this.sessionTimer = setTimeout(this.checkSessionExpiry, Math.min(remaining, 2147483647));
    },
    returnToMsp() {
      storage.removeItemsSessionStorage();
      window.location.assign('/msp');
    },
    async loadRestaurantBrand() {
      try {
        const profile = await getRestaurantProfile();
        this.restaurantLogo = imageUrl(profile.logo);
        this.restaurantName = profile.nameRestaurant || "Restaurante";
        this.logoFailed = false;
        if (!this.renewalChecked && this.authenticated) { this.renewalChecked = true; this.renewalNotice = profile.renewalNotice || null; this.renewalDialog = !!this.renewalNotice; }
      } catch (error) { /* La navegación sigue disponible si no se puede cargar el perfil. */ }
    },
    setAuthenticated(status) {
      this.authenticated = status;
      storage.saveAuthenticated(status);
      location.reload();
    },
    logout() {
      storage.removeItemsSessionStorage();
      this.handleSessionEnded();
      if(this.$route.name !== 'login') this.$router.replace({ name: "login" });
    },
  },
};
</script>

<style src="./styles/management.css"></style>
